import React, { useState } from 'react';
import useToastWithSound from '../../hooks/useToastWithSound';
import { MdGroup, MdPeople } from 'react-icons/md';
import api from '../../ui/api';
import useData from '../useData';
import useDirtyForm from '../useDirtyForm';
import { useNavIntent } from '../navIntent';
import PageShell, { Button } from '../../ui/PageShell';
import Table from '../../ui/Table';
import Modal from '../../ui/Modal';
import { EmptyState } from '../../ui/Card';
import { FormGrid, Field, Input, Textarea } from '../../ui/Form';
import PhoneInput from '../../ui/PhoneInput';
import { formatPhone } from '../../ui/phone';
import './families.css';

const emptyForm = {
  father_name: '', father_phone: '', father_job: '', last_name: '',
  mother_name: '', mother_phone: '', mother_last_name: '',
  address: '', amana: '', notes: '',
};

const dateOnly = (iso) => (iso ? iso.slice(0, 10) : '—');

/**
 * Families — one row per household, however many children it has.
 * `families` already carried father/mother/address in the schema; what
 * was missing was a screen to see and edit them, and a live student
 * count instead of a stored one (family_summaries VIEW, never stored —
 * same reasoning as section_student_counts: a count that can drift is
 * worse than no count).
 */
export default function Families() {
  const toast = useToastWithSound();
  const { data, loading, error, reload } = useData(() => api.families(), []);

  const [editing, setEditing] = useState(null); // family row or {} for new
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null); // family row whose students are shown
  const dirty = useDirtyForm(form);

  const openNew = () => { setForm(emptyForm); dirty.reset(emptyForm); setEditing({}); };
  const openEdit = (f) => {
    const next = {
      father_name: f.father_name || '', father_phone: f.father_phone || '',
      father_job: f.father_job || '', last_name: f.last_name || '', mother_name: f.mother_name || '',
      mother_phone: f.mother_phone || '', mother_last_name: f.mother_last_name || '',
      address: f.address || '', amana: f.amana || '', notes: f.notes || '',
    };
    setForm(next);
    dirty.reset(next);
    setEditing(f);
  };

  // Navbar search → «أفراد العائلة» / «تعديل العائلة».
  useNavIntent('families', (it) => {
    if (!data) return false;
    const f = data.find((r) => r.id === it.familyId);
    if (!f) return;
    if (it.edit) openEdit(f); else setViewing(f);
  }, [data]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.father_name.trim() && !form.mother_name.trim()) {
      return toast.error('اسم الأب أو الأم مطلوب على الأقل');
    }
    setSaving(true);
    try {
      if (editing.id) { await api.updateFamily(editing.id, form); toast.success('تم تحديث بيانات العائلة'); }
      else { await api.createFamily(form); toast.success('تمت إضافة العائلة'); }
      setEditing(null);
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const rows = (data || []).map((f) => ({
    ...f,
    parents_label: [f.father_name, f.mother_name].filter(Boolean).join(' و ') || '—',
    phone_label: f.father_phone || f.mother_phone || '',
  }));

  const familyTitle = (f) => [f.parents_label || f.father_name || f.mother_name, f.last_name && `(${f.last_name})`]
    .filter(Boolean).join(' ');

  return (
    <PageShell
      title="العائلات"
      description="عائلات الطلاب — إخوة وأخوات مرتبطون بعائلة واحدة"
      actions={<Button onClick={openNew}>+ عائلة جديدة</Button>}
    >
      {error && <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{error}</div></div>}

      <Table
        keyField="id"
        rows={rows}
        loading={loading}
        emptyText="لا توجد عائلات"
        searchPlaceholder="ابحث باسم الأب أو الأم أو رقم الهاتف..."
        searchableKeys={['father_name', 'mother_name', 'father_phone', 'mother_phone']}
        onEdit={openEdit}
        exportName="العائلات"
        renderActions={(f) => (
          <button
            type="button"
            className="ui-btn-mini ui-btn-mini-primary"
            onClick={() => setViewing(f)}
            title="عرض الطلاب المرتبطين بهذه العائلة"
          >
            <MdPeople size={15} /> الطلاب ({f.student_count})
          </button>
        )}
        columns={[
          { key: 'parents_label', label: 'العائلة', width: 2, filterable: false,
            render: (r) => (
              <div className="fam-title-cell">
                <strong>{familyTitle(r)}</strong>
                {r.father_job && <span className="fam-title-sub">{r.father_job}</span>}
              </div>
            ) },
          { key: 'phone_label', label: 'الهاتف', nowrap: true, filterable: false,
            render: (r) => (r.phone_label ? <bdi dir="ltr">{formatPhone(r.phone_label)}</bdi> : '—') },
          { key: 'address', label: 'العنوان' },
          { key: 'amana', label: 'الأمانة', nowrap: true },
          {
            key: 'student_count', label: 'عدد الطلاب', nowrap: true, align: 'center', filterable: false,
            render: (r) => (
              <span className={`fam-count-badge ${r.student_count > 1 ? 'is-multi' : r.student_count === 0 ? 'is-empty' : ''}`}>
                {r.student_count > 1 && <MdGroup size={13} />}
                {r.student_count}
              </span>
            ),
          },
          { key: 'created_at', label: 'تاريخ الإنشاء', nowrap: true, align: 'center', filterable: false,
            formatValue: dateOnly },
          { key: 'updated_at', label: 'آخر تحديث', nowrap: true, align: 'center', filterable: false,
            formatValue: dateOnly },
        ]}
      />

      {editing && (
        <Modal
          open
          size="lg"
          title={editing.id ? `تعديل: ${familyTitle(editing)}` : 'عائلة جديدة'}
          onClose={() => setEditing(null)}
          confirmClose={dirty.isDirty}
          footer={(requestClose) => (
            <>
              <Button variant="secondary" onClick={requestClose}>إلغاء</Button>
              <Button variant="success" onClick={save} loading={saving}>حفظ</Button>
            </>
          )}
        >
          <form onSubmit={save}>
            <FormGrid columns={2}>
              <Field label="اسم الأب"><Input value={form.father_name} onChange={set('father_name')} /></Field>
              <Field label="هاتف الأب"><PhoneInput value={form.father_phone} onChange={(v) => setForm((f) => ({ ...f, father_phone: v }))} /></Field>
              <Field label="مهنة الأب"><Input value={form.father_job} onChange={set('father_job')} /></Field>
              <Field label="الكنية" hint="تُستخدم لتركيب اسم الطالب تلقائياً عند إضافته"><Input value={form.last_name} onChange={set('last_name')} /></Field>
              <Field label="اسم الأم"><Input value={form.mother_name} onChange={set('mother_name')} /></Field>
              <Field label="هاتف الأم"><PhoneInput value={form.mother_phone} onChange={(v) => setForm((f) => ({ ...f, mother_phone: v }))} /></Field>
              <Field label="كنية الأم"><Input value={form.mother_last_name} onChange={set('mother_last_name')} /></Field>
              <Field label="الأمانة"><Input value={form.amana} onChange={set('amana')} /></Field>
              <Field label="العنوان" span><Input value={form.address} onChange={set('address')} /></Field>
              <Field label="ملاحظات" span><Textarea value={form.notes} onChange={set('notes')} /></Field>
            </FormGrid>
          </form>
        </Modal>
      )}

      {viewing && <FamilyStudentsModal family={viewing} onClose={() => setViewing(null)} />}
    </PageShell>
  );
}

/** The linked-students list for one family — "خالد، سارة، عمر" spelled
 *  out with each child's section, so the office can see who's a
 *  sibling of whom without opening every student one by one. */
function FamilyStudentsModal({ family, onClose }) {
  const { data, loading } = useData(() => api.familyStudents(family.id), [family.id]);
  const students = data || [];

  return (
    <Modal
      open
      size="md"
      title={`طلاب عائلة: ${[family.father_name, family.mother_name].filter(Boolean).join(' و ') || '—'}`}
      onClose={onClose}
      footer={<Button variant="secondary" onClick={onClose}>إغلاق</Button>}
    >
      {loading ? (
        <p>جارٍ التحميل...</p>
      ) : students.length === 0 ? (
        <EmptyState icon={<MdPeople size={40} />} title="لا يوجد طلاب" message="لا يوجد طلاب مرتبطون بهذه العائلة بعد." />
      ) : (
        <ul className="fam-student-list">
          {students.map((s) => (
            <li key={s.id} className="fam-student-row">
              <span className="fam-student-name">{s.name}</span>
              <span className={`ui-badge ${s.status === 'active' ? 'ui-badge-success' : 'ui-badge-neutral'}`}>
                {s.status === 'active' ? 'نشط' : 'مؤرشف'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
